import { create } from 'zustand';
import { taskApi, TimerStatus, taskErrorCode, taskErrorMessage } from '../services/taskService';

/**
 * Global state of the per-task timer.
 *
 * The authoritative value always comes from the server (`/tasks/timer/status`),
 * which returns the session together with the server's own clock. The store only
 * keeps the offset between the two clocks and derives a live counter from it, so
 * a wrong clock in the browser cannot distort the measurement and a page reload,
 * navigation or re-login restores the running timer exactly.
 */
interface TaskTimerState {
  session: TimerStatus['session'];
  /** serverNow - Date.now() at the moment of the last successful read (ms). */
  clockOffsetMs: number;
  loading: boolean;
  error: string;
  /** Set when the user tried to start a second timer; drives the confirm dialog. */
  conflict: { taskId: string; message: string } | null;

  refresh: () => Promise<void>;
  start: (taskId: string, force?: boolean) => Promise<boolean>;
  pause: (note?: string) => Promise<void>;
  stop: (note?: string) => Promise<void>;
  clearConflict: () => void;
  /** Seconds elapsed in the running session, computed against the server clock. */
  elapsedSeconds: () => number;
}

const applyStatus = (
  set: (partial: Partial<TaskTimerState>) => void,
  status: TimerStatus,
) => {
  set({
    session: status.session,
    clockOffsetMs: new Date(status.serverNow).getTime() - Date.now(),
    error: '',
  });
};

export const useTaskTimerStore = create<TaskTimerState>((set, get) => ({
  session: null,
  clockOffsetMs: 0,
  loading: false,
  error: '',
  conflict: null,

  refresh: async () => {
    set({ loading: true });
    try {
      applyStatus(set, await taskApi.timerStatus());
    } catch {
      // A failed poll must not wipe the timer the user can see.
      set({ error: '' });
    } finally {
      set({ loading: false });
    }
  },

  start: async (taskId, force = false) => {
    set({ loading: true, error: '' });
    try {
      applyStatus(set, await taskApi.timerStart(taskId, force));
      set({ conflict: null });
      return true;
    } catch (err) {
      if (taskErrorCode(err) === 'TIMER_RUNNING_ELSEWHERE') {
        set({ conflict: { taskId, message: taskErrorMessage(err) } });
      } else {
        set({ error: taskErrorMessage(err, 'Nie udało się uruchomić pomiaru czasu.') });
      }
      return false;
    } finally {
      set({ loading: false });
    }
  },

  pause: async (note) => {
    set({ loading: true });
    try {
      applyStatus(set, await taskApi.timerPause(note));
    } catch (err) {
      set({ error: taskErrorMessage(err, 'Nie udało się wstrzymać pomiaru.') });
    } finally {
      set({ loading: false });
    }
  },

  stop: async (note) => {
    set({ loading: true });
    try {
      applyStatus(set, await taskApi.timerStop(note));
    } catch (err) {
      set({ error: taskErrorMessage(err, 'Nie udało się zatrzymać pomiaru.') });
    } finally {
      set({ loading: false });
    }
  },

  clearConflict: () => set({ conflict: null }),

  elapsedSeconds: () => {
    const { session, clockOffsetMs } = get();
    if (!session) return 0;
    const serverNow = Date.now() + clockOffsetMs;
    return Math.max(0, Math.round((serverNow - new Date(session.startedAt).getTime()) / 1000));
  },
}));
